import { useState, useEffect, useCallback, useRef } from 'react'
import { MAX_FILE_BYTES, MAX_ATTACHMENTS, MAX_TOTAL_FILE_BYTES, type AttachedFile } from '@/lib/api'

interface UseFileAttachmentResult {
  pendingFiles: AttachedFile[]
  fileError: string | null
  isDragging: boolean
  handleAttachPress: () => void
  handleCameraPress: () => void
  /** Attach already-encoded files (e.g. a voice-notes transcript). Same caps as picked files. */
  addAttachedFiles: (files: AttachedFile[]) => void
  removePendingFile: (index: number) => void
  clearPendingFiles: () => void
  clearFileError: () => void
}

const mb = (bytes: number) => (bytes / 1024 / 1024).toFixed(1)

// Phone photos run 3–5 MB, which would fill the 12 MB request budget after
// three shots. Anything the operator attaches is viewed on screens and read by
// the model — 2048px JPEG is ample for both — so large images are downscaled
// before they count against the caps, letting all MAX_ATTACHMENTS photos fit.
// Every failure path returns the original file untouched (HEIC on non-Safari,
// SVG, decode errors): worse compression, never a lost attachment.
const DOWNSCALE_THRESHOLD_BYTES = 1.5 * 1024 * 1024
const DOWNSCALE_MAX_DIMENSION = 2048
const DOWNSCALE_TYPES = ['image/jpeg', 'image/png', 'image/webp']

async function downscaleImage(f: File): Promise<File> {
  if (!DOWNSCALE_TYPES.includes(f.type) || f.size <= DOWNSCALE_THRESHOLD_BYTES) return f
  try {
    const bitmap = await createImageBitmap(f)
    const scale = Math.min(1, DOWNSCALE_MAX_DIMENSION / Math.max(bitmap.width, bitmap.height))
    const canvas = document.createElement('canvas')
    canvas.width = Math.max(1, Math.round(bitmap.width * scale))
    canvas.height = Math.max(1, Math.round(bitmap.height * scale))
    const ctx = canvas.getContext('2d')
    if (!ctx) return f
    ctx.drawImage(bitmap, 0, 0, canvas.width, canvas.height)
    bitmap.close()
    const blob = await new Promise<Blob | null>((resolve) => canvas.toBlob(resolve, 'image/jpeg', 0.85))
    if (!blob || blob.size >= f.size) return f
    const name = f.name.replace(/\.(png|webp|jpeg|jpg)$/i, '') + '.jpg'
    return new File([blob], name, { type: 'image/jpeg' })
  } catch {
    return f
  }
}

interface UseFileAttachmentOptions {
  /**
   * Whether the document-level drag-and-drop and paste listeners are active.
   * Defaults to true (the full-page chat behaviour). The assistant panel binds
   * this to its open state — while the panel is closed, a paste or drop on a
   * data screen must not silently attach to a chat nobody is looking at.
   */
  listenGlobal?: boolean
}

export function useFileAttachment(
  onFiles?: (files: AttachedFile[]) => void,
  options?: UseFileAttachmentOptions,
): UseFileAttachmentResult {
  const listenGlobal = options?.listenGlobal ?? true
  const [pendingFiles, setPendingFiles] = useState<AttachedFile[]>([])
  const [fileError, setFileError] = useState<string | null>(null)
  const [isDragging, setIsDragging] = useState(false)

  const fileInputRef = useRef<HTMLInputElement | null>(null)
  const cameraInputRef = useRef<HTMLInputElement | null>(null)

  // Selections arrive from four independent sources (picker, camera, drop, paste)
  // and each read is async, so the cap has to be checked against the live list
  // rather than the state captured when the handler was created — otherwise two
  // quick drops of 2 files each both see "0 attached" and land 4.
  const pendingRef = useRef<AttachedFile[]>([])

  const commit = useCallback((next: AttachedFile[]) => {
    pendingRef.current = next
    setPendingFiles(next)
  }, [])

  const readFile = (f: File) =>
    new Promise<AttachedFile>((resolve, reject) => {
      const reader = new FileReader()
      reader.onload = () => {
        const dataUrl = reader.result as string
        resolve({
          name: f.name,
          mimeType: f.type || 'application/octet-stream',
          data: dataUrl.split(',')[1],
          size: f.size,
        })
      }
      reader.onerror = () => reject(reader.error)
      reader.readAsDataURL(f)
    })

  const processFiles = useCallback(async (incoming: File[]) => {
    if (incoming.length === 0) return

    const current = pendingRef.current
    const problems: string[] = []

    // Take only what there is room for; say so rather than dropping silently.
    const room = Math.max(0, MAX_ATTACHMENTS - current.length)
    const rawCandidates = incoming.slice(0, room)
    if (rawCandidates.length < incoming.length) {
      problems.push(`You can attach up to ${MAX_ATTACHMENTS} files.`)
    }

    // Downscale big photos BEFORE the size checks, so the caps apply to what
    // is actually sent.
    const candidates = await Promise.all(rawCandidates.map(downscaleImage))

    const accepted: File[] = []
    let total = current.reduce((sum, f) => sum + f.size, 0)
    for (const f of candidates) {
      if (f.size > MAX_FILE_BYTES) {
        problems.push(`File too large — maximum ${mb(MAX_FILE_BYTES)} MB (${f.name}: ${mb(f.size)} MB)`)
        continue
      }
      if (total + f.size > MAX_TOTAL_FILE_BYTES) {
        problems.push(`Attachments too large — ${mb(MAX_TOTAL_FILE_BYTES)} MB total maximum.`)
        continue
      }
      total += f.size
      accepted.push(f)
    }

    if (accepted.length === 0) {
      setFileError(problems.length ? problems.join(' ') : null)
      return
    }

    // A file the browser cannot read (an unplugged drive, a revoked permission)
    // must not take the rest of the drop with it: Promise.all would reject the
    // whole batch, and nothing catches that — so every file was lost silently.
    const settled = await Promise.allSettled(accepted.map(readFile))
    const read: AttachedFile[] = []
    settled.forEach((outcome, i) => {
      if (outcome.status === 'fulfilled') read.push(outcome.value)
      else problems.push(`Could not read ${accepted[i].name}.`)
    })

    setFileError(problems.length ? problems.join(' ') : null)
    if (read.length === 0) return

    // Re-read the ref: another source may have added files while these were read.
    const next = [...pendingRef.current, ...read].slice(0, MAX_ATTACHMENTS)
    commit(next)
    onFiles?.(read)
  }, [commit, onFiles])

  // Keep a stable ref to processFiles so event handlers created once stay current
  const processFilesRef = useRef(processFiles)
  processFilesRef.current = processFiles

  // Hidden file input — `multiple` so all three can be picked in one go
  useEffect(() => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*,application/pdf,.txt,.csv'
    input.multiple = true
    input.style.cssText = 'position:fixed;top:-200px;left:-200px;opacity:0'
    input.onchange = (e) => {
      const picked = Array.from((e.target as HTMLInputElement).files ?? [])
      if (picked.length) processFilesRef.current(picked)
      // Reset so picking the SAME file again still fires change.
      ;(e.target as HTMLInputElement).value = ''
    }
    document.body.appendChild(input)
    fileInputRef.current = input
    return () => {
      document.body.removeChild(input)
      fileInputRef.current = null
    }
  }, [])

  // Hidden camera input — one shot per press, so no `multiple` here
  useEffect(() => {
    const input = document.createElement('input')
    input.type = 'file'
    input.accept = 'image/*'
    input.setAttribute('capture', 'environment')
    input.style.cssText = 'position:fixed;top:-200px;left:-200px;opacity:0'
    input.onchange = (e) => {
      const f = (e.target as HTMLInputElement).files?.[0]
      if (f) processFilesRef.current([f])
      ;(e.target as HTMLInputElement).value = ''
    }
    document.body.appendChild(input)
    cameraInputRef.current = input
    return () => {
      document.body.removeChild(input)
      cameraInputRef.current = null
    }
  }, [])

  // Drag-and-drop
  useEffect(() => {
    if (!listenGlobal) return
    let dragCounter = 0

    const onDragEnter = () => { dragCounter++; setIsDragging(true) }
    const onDragLeave = () => {
      dragCounter--
      if (dragCounter <= 0) { dragCounter = 0; setIsDragging(false) }
    }
    const onDragOver = (e: Event) => { e.preventDefault() }
    const onDrop = (e: Event) => {
      e.preventDefault()
      dragCounter = 0
      setIsDragging(false)
      const dropped = Array.from((e as DragEvent).dataTransfer?.files ?? [])
      if (dropped.length) processFilesRef.current(dropped)
    }

    document.addEventListener('dragenter', onDragEnter)
    document.addEventListener('dragleave', onDragLeave)
    document.addEventListener('dragover', onDragOver)
    document.addEventListener('drop', onDrop)

    return () => {
      document.removeEventListener('dragenter', onDragEnter)
      document.removeEventListener('dragleave', onDragLeave)
      document.removeEventListener('dragover', onDragOver)
      document.removeEventListener('drop', onDrop)
    }
  }, [listenGlobal])

  // Paste from clipboard
  useEffect(() => {
    if (!listenGlobal) return
    const onPaste = (e: Event) => {
      const pasted = Array.from((e as ClipboardEvent).clipboardData?.files ?? [])
      if (!pasted.length) return
      e.preventDefault()
      processFilesRef.current(pasted)
    }
    document.addEventListener('paste', onPaste)
    return () => document.removeEventListener('paste', onPaste)
  }, [listenGlobal])

  // Programmatic attachments (the voice-notes transcript) skip the File read but
  // go through the same cap checks as every other source.
  const addAttachedFiles = useCallback((incoming: AttachedFile[]) => {
    if (incoming.length === 0) return
    const current = pendingRef.current
    const problems: string[] = []

    const room = Math.max(0, MAX_ATTACHMENTS - current.length)
    const candidates = incoming.slice(0, room)
    if (candidates.length < incoming.length) {
      problems.push(`You can attach up to ${MAX_ATTACHMENTS} files.`)
    }

    const accepted: AttachedFile[] = []
    let total = current.reduce((sum, f) => sum + f.size, 0)
    for (const f of candidates) {
      if (f.size > MAX_FILE_BYTES) {
        problems.push(`File too large — maximum ${mb(MAX_FILE_BYTES)} MB (${f.name}: ${mb(f.size)} MB)`)
        continue
      }
      if (total + f.size > MAX_TOTAL_FILE_BYTES) {
        problems.push(`Attachments too large — ${mb(MAX_TOTAL_FILE_BYTES)} MB total maximum.`)
        continue
      }
      total += f.size
      accepted.push(f)
    }

    setFileError(problems.length ? problems.join(' ') : null)
    if (accepted.length === 0) return
    commit([...pendingRef.current, ...accepted].slice(0, MAX_ATTACHMENTS))
  }, [commit])

  const handleAttachPress = useCallback(() => {
    fileInputRef.current?.click()
  }, [])

  const handleCameraPress = useCallback(() => {
    cameraInputRef.current?.click()
  }, [])

  const removePendingFile = useCallback((index: number) => {
    commit(pendingRef.current.filter((_, i) => i !== index))
    setFileError(null)
  }, [commit])

  const clearPendingFiles = useCallback(() => commit([]), [commit])
  const clearFileError = useCallback(() => setFileError(null), [])

  return {
    pendingFiles,
    fileError,
    isDragging,
    handleAttachPress,
    handleCameraPress,
    addAttachedFiles,
    removePendingFile,
    clearPendingFiles,
    clearFileError,
  }
}
